import {
  DeleteOutlined,
  DownOutlined,
  HolderOutlined,
  UpOutlined
} from "@ant-design/icons"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Col, Flex, Row, Tooltip, Typography } from "antd"
import { type Dayjs } from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker.tsx"
import InputNumber from "../../../../components/Input/InputNumber.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import {
  columnFlex,
  stretchColumnFlex,
  MILESTONE_EDITOR_COLUMN_WIDTHS as widths
} from "./lead-scope-milestone-layout.ts"
import {
  type MilestoneTimelineIssue,
  type PlanningWindow
} from "./lead-scope-milestone-timeline.ts"
import {
  milestoneDuration,
  milestonePayable,
  milestonePayableExceedsReceivable,
  milestoneReceivable,
  withMilestoneDuration,
  withMilestoneEndDate,
  withMilestonePayable,
  withMilestonePrice,
  withMilestoneReceivable,
  withMilestoneStartDate
} from "./lead-scope-milestones.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography

interface LeadScopeMilestoneRowProps {
  milestone: LeadFormDataPhase
  index: number
  total: number
  saving?: boolean
  planning?: PlanningWindow
  timelineIssues?: MilestoneTimelineIssue[]
  nameError?: string | null
  onChange: (index: number, milestone: LeadFormDataPhase) => void
  onMove: (from: number, to: number) => void
  onRemove: (index: number) => void
}

function LeadScopeMilestoneRow({
  milestone,
  index,
  total,
  saving,
  planning,
  timelineIssues = [],
  nameError,
  onChange,
  onMove,
  onRemove
}: LeadScopeMilestoneRowProps) {
  const { t } = useTranslation()
  const { parseDate, userFormat, normalizeDate } = useDateFormat()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: milestone.id })

  const toIsoDay = (value: Dayjs | null) =>
    value?.isValid() ? value.format(DATE_FORMAT) : null

  const patch = (next: Partial<LeadFormDataPhase>) =>
    onChange(index, { ...milestone, ...next })

  const financialMismatch = milestonePayableExceedsReceivable(milestone)

  const timelineHint = (issues: MilestoneTimelineIssue[]) => {
    const hits = issues.filter((issue) => timelineIssues.includes(issue))
    if (!hits.length) return undefined

    return hits
      .map((issue) =>
        t(`leadWorkspace.scope.milestones.timeline.row.${issue}`, {
          planStart: normalizeDate(
            planning?.start_date,
            userFormat ?? undefined
          ),
          planEnd: normalizeDate(planning?.end_date, userFormat ?? undefined)
        })
      )
      .join(" ")
  }
  const startHint = timelineHint(["startsBeforePlan", "endBeforeStart"])
  const endHint = timelineHint(["endsAfterPlan", "endBeforeStart"])

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 20, opacity: 0.5 } : {})
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`milestone-row-${index}`}
      className={
        "border-b border-border even:bg-primary/5 last:border-none py-2"
      }
    >
      <Row className={"flex-nowrap"} gutter={4} align={"top"}>
        <Col flex={columnFlex(widths.actions)}>
          <Flex gap={2}>
            <Button
              size={"small"}
              icon={<HolderOutlined />}
              style={{ cursor: "move" }}
              aria-label={t("leadWorkspace.scope.milestones.drag", {
                position: index + 1
              })}
              {...attributes}
              {...listeners}
            />
            <Button
              size={"small"}
              icon={<UpOutlined />}
              disabled={saving || index === 0}
              aria-label={t("leadWorkspace.scope.milestones.moveUp", {
                position: index + 1
              })}
              onClick={() => onMove(index, index - 1)}
            />
            <Button
              size={"small"}
              icon={<DownOutlined />}
              disabled={saving || index === total - 1}
              aria-label={t("leadWorkspace.scope.milestones.moveDown", {
                position: index + 1
              })}
              onClick={() => onMove(index, index + 1)}
            />
          </Flex>
        </Col>
        <Col flex={stretchColumnFlex(widths.name)}>
          <TextInput
            disabled={saving}
            status={nameError ? "error" : undefined}
            aria-invalid={!!nameError}
            placeholder={t("leadWorkspace.scope.milestones.namePlaceholder")}
            aria-label={t("leadWorkspace.scope.milestones.name", {
              position: index + 1
            })}
            value={milestone.name ?? ""}
            onChange={(event) => patch({ name: event.target.value })}
          />
          {nameError && (
            <Text
              type={"danger"}
              role={"alert"}
              className={"text-xs"}
              data-testid={`milestone-name-error-${index}`}
            >
              {nameError}
            </Text>
          )}
        </Col>
        <Col flex={columnFlex(widths.date)}>
          <Tooltip title={startHint}>
            <CustomDatePicker
              className={"w-full"}
              disabled={saving}
              status={startHint ? "warning" : undefined}
              aria-label={t("leadWorkspace.scope.milestones.start", {
                position: index + 1
              })}
              value={parseDate(milestone.start_date)}
              onChange={(next) =>
                onChange(
                  index,
                  withMilestoneStartDate(milestone, toIsoDay(next))
                )
              }
            />
          </Tooltip>
        </Col>
        <Col flex={columnFlex(widths.date)}>
          <Tooltip title={endHint}>
            <CustomDatePicker
              className={"w-full"}
              disabled={saving}
              status={endHint ? "warning" : undefined}
              aria-label={t("leadWorkspace.scope.milestones.end", {
                position: index + 1
              })}
              value={parseDate(milestone.end_date)}
              onChange={(next) =>
                onChange(index, withMilestoneEndDate(milestone, toIsoDay(next)))
              }
            />
          </Tooltip>
        </Col>
        <Col flex={columnFlex(widths.duration)}>
          <InputNumber
            min={0}
            max={99999999}
            precision={0}
            suffix={
              milestoneDuration(milestone) === 1
                ? t("leadWorkspace.scope.milestones.units.day")
                : t("leadWorkspace.scope.milestones.units.days")
            }
            className={"w-full"}
            disabled={saving}
            aria-label={t("leadWorkspace.scope.milestones.duration", {
              position: index + 1
            })}
            value={
              milestone.duration_days == null
                ? undefined
                : Number(milestone.duration_days)
            }
            onChange={(next) =>
              onChange(index, withMilestoneDuration(milestone, next))
            }
          />
        </Col>
        <Col flex={columnFlex(widths.money)}>
          <InputNumber
            min={0}
            max={999999999.99}
            step={0.1}
            precision={2}
            prefix={"$"}
            className={"w-full"}
            disabled={saving}
            aria-label={t("leadWorkspace.scope.milestones.price", {
              position: index + 1
            })}
            value={Number(milestone.budget?.budget ?? 0)}
            onChange={(next) =>
              onChange(index, withMilestonePrice(milestone, next))
            }
          />
        </Col>
        <Col flex={columnFlex(widths.money)}>
          <Tooltip
            title={
              financialMismatch
                ? t("leadWorkspace.scope.milestones.payableExceedsReceivable")
                : undefined
            }
          >
            <InputNumber
              min={0}
              max={999999999.99}
              step={0.1}
              precision={2}
              prefix={"$"}
              className={"w-full"}
              disabled={saving}
              status={financialMismatch ? "warning" : undefined}
              aria-label={t("leadWorkspace.scope.milestones.receivable", {
                position: index + 1
              })}
              value={milestoneReceivable(milestone)}
              onChange={(next) =>
                onChange(index, withMilestoneReceivable(milestone, next))
              }
            />
          </Tooltip>
        </Col>
        <Col flex={columnFlex(widths.money)}>
          <Tooltip
            title={
              financialMismatch
                ? t("leadWorkspace.scope.milestones.payableExceedsReceivable")
                : undefined
            }
          >
            <InputNumber
              min={0}
              max={999999999.99}
              step={0.1}
              precision={2}
              prefix={"$"}
              className={"w-full"}
              disabled={saving}
              status={financialMismatch ? "warning" : undefined}
              aria-label={t("leadWorkspace.scope.milestones.payable", {
                position: index + 1
              })}
              value={milestonePayable(milestone)}
              onChange={(next) =>
                onChange(index, withMilestonePayable(milestone, next))
              }
            />
          </Tooltip>
        </Col>
        <Col flex={columnFlex(widths.remove)}>
          <Button
            size={"small"}
            danger
            icon={<DeleteOutlined />}
            disabled={saving}
            aria-label={t("leadWorkspace.scope.milestones.remove", {
              position: index + 1
            })}
            onClick={() => onRemove(index)}
          />
        </Col>
      </Row>
    </div>
  )
}

export { LeadScopeMilestoneRow }
